const EXPIRES_AT_KEY = "nexus_expires_at";

/** Claims in a Nexus JWT. Access groups are not in the token — exchange/refresh return them as `accessIds`. */
export interface NexusTokenPayload {
  sub: string;
  /** @deprecated Rollout-compat claim; only a fallback when an older API pod omits `accessIds`. */
  acs?: string[];
  email: string;
  iat: number;
  exp: number;
  iss: "nexus";
  jti: string;
}

// ─── Session expiry (localStorage-backed) ──────────────────────────────────

export function storeExpiresAt(expiresAt: number): void {
  localStorage.setItem(EXPIRES_AT_KEY, String(expiresAt));
}

export function getExpiresAt(): number | null {
  const raw = localStorage.getItem(EXPIRES_AT_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function clearExpiresAt(): void {
  localStorage.removeItem(EXPIRES_AT_KEY);
}

/** Buffer (seconds) before actual expiry to trigger proactive refresh. */
const EXPIRY_BUFFER_SECONDS = 60;

const MAX_REASONABLE_TTL_SECONDS = 7200; // 2x the 1-hour access token TTL

export function isSessionExpiringSoon(bufferSeconds = EXPIRY_BUFFER_SECONDS): boolean {
  const expiresAt = getExpiresAt();
  if (expiresAt === null) return true;

  const nowSeconds = Math.floor(Date.now() / 1000);

  // Sanity guard: if expiresAt is unreasonably far in the future, treat as expired
  if (expiresAt > nowSeconds + MAX_REASONABLE_TTL_SECONDS) return true;

  return nowSeconds > expiresAt - bufferSeconds;
}

// ─── Token parsing (still used for tests/mocks) ────────────────────────────

function base64UrlEncode(obj: unknown): string {
  const json = JSON.stringify(obj);
  return btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(str: string): string {
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4) base64 += "=";
  return atob(base64);
}

export function parseToken(token: string): NexusTokenPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    if (
      typeof payload.sub !== "string" ||
      typeof payload.email !== "string" ||
      typeof payload.exp !== "number" ||
      payload.iss !== "nexus"
    ) {
      return null;
    }
    return payload as NexusTokenPayload;
  } catch {
    return null;
  }
}

function randomId(): string {
  return crypto.randomUUID().replace(/-/g, "");
}

export function createMockToken(email: string): string {
  const now = Math.floor(Date.now() / 1000);
  const header = { alg: "HS256", typ: "JWT" };
  const payload: NexusTokenPayload = {
    sub: randomId(),
    email,
    iat: now,
    exp: now + 3600,
    iss: "nexus",
    jti: randomId(),
  };
  const sig = "mock_signature";
  return `${base64UrlEncode(header)}.${base64UrlEncode(payload)}.${base64UrlEncode(sig)}`;
}
